import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Users } from 'lucide-react';

export default function SymbolCard({ symbol, noLink = false, onCardClick }) {
  const {
    ticker, name, exchange,
    analyst_count, prediction_count,
    bullish_count, bearish_count, neutral_count,
    latest_prediction_at, latest_close_price,
  } = symbol;

  const total = (bullish_count + bearish_count + neutral_count) || 1;
  const bullPct = Math.round((bullish_count / total) * 100);
  const bearPct = Math.round((bearish_count / total) * 100);
  const neutPct = 100 - bullPct - bearPct;

  const latestDate = latest_prediction_at ? new Date(latest_prediction_at) : null;

  return (
    <Link
      to={`/analysts/symbols/${exchange}/${ticker}`}
      onClick={noLink ? (e) => { e.preventDefault(); onCardClick?.(); } : undefined}
      className={`bg-white rounded-xl border border-gray-100 p-4 transition-all flex flex-col gap-3 ${
        noLink ? 'cursor-pointer hover:shadow-md hover:border-gray-200' : 'hover:shadow-md hover:border-gray-200'
      }`}
    >
      {/* Ticker + exchange badge */}
      <div className="flex items-start justify-between">
        <div>
          <span className="text-xl font-bold text-gray-900">{ticker}</span>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{name}</p>
        </div>
        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md font-medium shrink-0">
          {exchange}
        </span>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Users size={12} />
          {analyst_count} analyst{analyst_count !== 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1">
          <BarChart3 size={12} />
          {prediction_count} call{prediction_count !== 1 ? 's' : ''}
        </span>
        {latest_close_price != null && (
          <span className="ml-auto font-semibold text-gray-800">
            ${Number(latest_close_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        )}
      </div>

      {/* Sentiment bar */}
      <div>
        <div className="flex h-1.5 rounded-full overflow-hidden">
          {bullPct > 0 && <div className="bg-emerald-500" style={{ width: `${bullPct}%` }} />}
          {neutPct > 0 && <div className="bg-gray-300" style={{ width: `${neutPct}%` }} />}
          {bearPct > 0 && <div className="bg-red-400" style={{ width: `${bearPct}%` }} />}
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-xs">
          <span className="text-emerald-600">{bullish_count} Bull</span>
          <span className="text-gray-400">{neutral_count} Neu</span>
          <span className="text-red-500">{bearish_count} Bear</span>
          {latestDate && (
            <span className="ml-auto text-gray-400">
              {latestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
